import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputTextComponent } from '@view/shared/molecules/input-text/input-text.component';
import { FormDropdownComponent } from '@view/shared/molecules/form-dropdown/form-dropdown.component';
import { DropdownItem } from '@view/shared/molecules/dropdown/dropdown.component';
import { FormDateComponent } from '@view/shared/molecules/form-date/form-date.component';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  KinshipType,
  FamilyMemberData,
  ExamTypeInfo
} from '@infra-services/exam/exam-type.interface';
import { ExamTypeService } from '@infra-services/exam/exam-type.service';
import {
  colombianPhoneValidator,
  emailFormatValidator,
  maxDateValidator
} from '@view/shared/validators/form-validators';

@Component({
  selector: 'app-family-member-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextComponent,
    FormDropdownComponent,
    FormDateComponent,
    ButtonComponent
  ],
  template: `
    <div class="modal-overlay" *ngIf="isOpen()">
      <div class="modal-container">
        <div class="modal-header">
          <h3>{{ getTitle() }}</h3>
          <button class="close-button" (click)="close()">×</button>
        </div>

        <div class="modal-body">
          <form [formGroup]="familyMemberForm">
            <!-- Tipo de Parentesco -->
            <div class="form-row">
              <app-form-dropdown
                label="Tipo de Parentesco"
                formControlName="tipoParentesco"
                [items]="kinshipTypeItems"
              ></app-form-dropdown>
            </div>

            <!-- Datos de Identificación -->
            <div class="form-section">
              <h4>Datos de Identificación</h4>
              <div class="form-row two-columns">
                <app-form-dropdown
                  label="Tipo de Documento"
                  formControlName="identifierTypeCode"
                  [items]="identifierTypeItems"
                ></app-form-dropdown>
                <app-input-text
                  label="Número de Documento"
                  formControlName="idNumber"
                  [required]="true"
                ></app-input-text>
              </div>
            </div>

            <!-- Datos Personales -->
            <div class="form-section">
              <h4>Datos Personales</h4>
              <div class="form-row two-columns">
                <app-input-text
                  label="Primer Nombre"
                  formControlName="givenName"
                  [required]="true"
                ></app-input-text>
                <app-input-text
                  label="Segundo Nombre"
                  formControlName="middleName"
                ></app-input-text>
              </div>
              <div class="form-row two-columns">
                <app-input-text
                  label="Primer Apellido"
                  formControlName="familyName"
                  [required]="true"
                ></app-input-text>
                <app-input-text
                  label="Segundo Apellido"
                  formControlName="maternalFamilyName"
                ></app-input-text>
              </div>
              <div class="form-row two-columns">
                <app-form-dropdown
                  label="Sexo"
                  formControlName="administrativeSex"
                  [items]="sexItems"
                ></app-form-dropdown>
                <app-form-date
                  label="Fecha de Nacimiento"
                  formControlName="birthDate"
                  [required]="true"
                ></app-form-date>
              </div>
            </div>

            <!-- Datos de Contacto -->
            <div class="form-section">
              <h4>Datos de Contacto</h4>
              <div class="form-row two-columns">
                <app-input-text
                  label="Teléfono"
                  formControlName="telephoneNumber"
                ></app-input-text>
                <app-input-text
                  label="Email"
                  formControlName="emailAddress"
                  type="email"
                ></app-input-text>
              </div>
              <div class="form-row two-columns">
                <app-input-text
                  label="Estado Civil"
                  formControlName="maritalStatus"
                  placeholder="Ej: Soltero, Casado"
                ></app-input-text>
              </div>
            </div>

            <!-- Dirección -->
            <div class="form-section">
              <h4>Dirección</h4>
              <div class="form-row">
                <app-input-text
                  label="Dirección (opcional)"
                  formControlName="streetAddress"
                ></app-input-text>
              </div>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <app-button
            label="Cancelar"
            variant="secondary"
            (click)="close()"
          ></app-button>
          <app-button
            label="Agregar Familiar"
            variant="primary"
            [disabled]="familyMemberForm.invalid"
            (click)="save()"
          ></app-button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      }

      .modal-container {
        background: white;
        border-radius: 8px;
        width: 90%;
        max-width: 700px;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      }

      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #e0e0e0;

        h3 {
          margin: 0;
          font-size: 1.25rem;
          color: #333;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #666;

          &:hover {
            color: #333;
          }
        }
      }

      .modal-body {
        padding: 1.5rem;
      }

      .form-section {
        margin-bottom: 1.5rem;

        h4 {
          margin: 0 0 1rem;
          font-size: 1rem;
          color: #555;
          border-bottom: 1px solid #eee;
          padding-bottom: 0.5rem;
        }
      }

      .form-row {
        margin-bottom: 1rem;

        &.two-columns {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        &.three-columns {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 1rem;
        }
      }

      .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
        padding: 1rem 1.5rem;
        border-top: 1px solid #e0e0e0;
      }
    `
  ]
})
export class FamilyMemberModalComponent implements OnInit {
  @Input() identifierTypes: { id: string; name: string }[] = [];
  @Input() examTypeInfo: ExamTypeInfo | null = null;
  @Input() patientSex = '';

  @Output() familyMemberAdded = new EventEmitter<FamilyMemberData>();
  @Output() modalClosed = new EventEmitter<void>();

  private readonly fb = inject(FormBuilder);
  private readonly examTypeService = inject(ExamTypeService);

  isOpen = signal(false);
  familyMemberForm!: FormGroup;
  kinshipTypeItems: DropdownItem[] = [];
  identifierTypeItems: DropdownItem[] = [];

  sexItems: DropdownItem[] = [
    { label: 'Masculino', value: 'M' },
    { label: 'Femenino', value: 'F' }
  ];

  ngOnInit(): void {
    this.initForm();
    this.updateKinshipTypeOptions();
    this.updateIdentifierTypeItems();
  }

  private updateIdentifierTypeItems(): void {
    this.identifierTypeItems = this.identifierTypes.map((type) => ({
      label: type.name,
      value: type.id
    }));
  }

  private initForm(): void {
    this.familyMemberForm = this.fb.group({
      tipoParentesco: ['', Validators.required],
      idNumber: ['', Validators.required],
      identifierTypeCode: ['', Validators.required],
      givenName: ['', Validators.required],
      middleName: [''],
      familyName: ['', Validators.required],
      maternalFamilyName: [''],
      emailAddress: ['', [emailFormatValidator()]],
      birthDate: ['', [Validators.required, maxDateValidator()]],
      administrativeSex: ['', Validators.required],
      maritalStatus: [''],
      race: [''],
      telephoneNumber: ['', [colombianPhoneValidator()]],
      streetAddress: [''],
      city: [''],
      state: [''],
      country: ['']
    });

    this.familyMemberForm
      .get('tipoParentesco')
      ?.valueChanges.subscribe((type) => {
        this.autoSetSexByKinshipType(type);
      });
  }

  private autoSetSexByKinshipType(type: string): void {
    const sexControl = this.familyMemberForm.get('administrativeSex');
    if (!sexControl) {
      return;
    }

    if (type === KinshipType.PADRE || type === KinshipType.FUTURO_PADRE) {
      sexControl.setValue('M');
    } else if (
      type === KinshipType.MADRE ||
      type === KinshipType.FUTURA_MADRE
    ) {
      sexControl.setValue('F');
    }
  }

  private updateKinshipTypeOptions(): void {
    if (!this.examTypeInfo) {
      this.kinshipTypeItems = [];
      return;
    }

    const types = this.examTypeService.getRequiredKinshipTypes(
      this.examTypeInfo,
      this.patientSex
    );
    this.kinshipTypeItems = types.map((type) => ({
      label: this.examTypeService.getKinshipTypeLabel(type),
      value: type
    }));
  }

  getTitle(): string {
    const type = this.familyMemberForm?.get('tipoParentesco')?.value;
    if (type) {
      return `Agregar ${this.examTypeService.getKinshipTypeLabel(type)}`;
    }
    return 'Agregar Familiar';
  }

  open(): void {
    this.isOpen.set(true);
    this.updateKinshipTypeOptions();
    this.updateIdentifierTypeItems();
  }

  close(): void {
    this.isOpen.set(false);
    this.familyMemberForm.reset();
    this.modalClosed.emit();
  }

  save(): void {
    if (this.familyMemberForm.invalid) {
      Object.keys(this.familyMemberForm.controls).forEach((key) => {
        this.familyMemberForm.get(key)?.markAsTouched();
      });
      return;
    }

    const formValue = this.familyMemberForm.getRawValue();

    const familyMember: FamilyMemberData = {
      tipoParentesco: formValue.tipoParentesco as KinshipType,
      datosPaciente: {
        idNumber: formValue.idNumber,
        identifierTypeCode: formValue.identifierTypeCode,
        givenName: formValue.givenName,
        middleName: formValue.middleName,
        familyName: formValue.familyName,
        maternalFamilyName: formValue.maternalFamilyName,
        emailAddress: formValue.emailAddress,
        birthDate: this.formatDate(formValue.birthDate),
        administrativeSex: formValue.administrativeSex,
        maritalStatus: formValue.maritalStatus,
        race: formValue.race,
        telephoneNumber: formValue.telephoneNumber,
        streetAddress: formValue.streetAddress,
        city: formValue.city,
        state: formValue.state,
        country: formValue.country
      }
    };

    this.familyMemberAdded.emit(familyMember);
    this.close();
  }

  private formatDate(date: string | Date): string {
    if (!date) {
      return '';
    }
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return Number.isNaN(dateObj.getTime())
      ? String(date)
      : dateObj.toISOString();
  }
}
